import * as Util from "../common/util";
import * as Data from "../interfaces/data";
import * as User from "../interfaces/user";

export interface IAuthenticateUserRequest {
    UserName: string;
    Password: string;
}

export interface IAuthenticationResponse {
    // if true, then the user is authenticated and can start using the system.
    // if false, then the authentication failed.  The other fields in the
    // response will indicate why the authentication failed.
    IsAuthenticated: boolean;

    // if IsAuthenticated is true, this contains the token that must
    // be passed on all future web service requests.
    AuthenticationToken: string;
    ReportToken: string;

    // if IsAuthenticated is true, this field contains additional information
    // about the user, such as name, permissions, etc.
    UserSession: User.IUserSession;

    // if true, then the user name and password combination are not valid.
    UserNamePasswordInvalid: boolean;

    // if true, then the users password has expired and must be changed.
    MustChangePassword: boolean;

    // if MustChangePassword is true, this is the token needed to reset the password
    PasswordResetToken: string;

    // if true, then the user has exceeded the maximum number of invalid
    // attempts allowed in a specific time period.  The account
    // is temporarily locked.
    ExceededMaximumAttempts: boolean;

    // if ExceededMaximumAttempts is true, this field contains the number
    // of minutes the account will remain locked.
    AccountLockedLength: number;
}

export function AuthenticateUser(request: IAuthenticateUserRequest): JQueryDeferred<Data.IResponse<IAuthenticationResponse>> {
    return Util.callWebService(
        "/api/Authentication/AuthenticateUser",
        "POST",
        request);
}

export interface IAuthenticateJWTRequest {
    IDToken: string;
    NOnce: string | null;
}

export function AuthenticateJWT(request: IAuthenticateJWTRequest): JQueryDeferred<Data.IResponse<IAuthenticationResponse>> {
    return Util.callWebService(
        "/api/Authentication/AuthenticateJWT",
        "POST",
        request);
}

export interface IAuthenticateUserWithTokenRequest {
    Token: string;
}

export interface IAuthenticateUserWithTokenResponse {
    /// if true, then the token is valid and the requested
    /// action can be performed.
    IsAuthenticated: boolean;

    /// if IsAuthenticated is true, this contains the token that must
    /// be passed on all future web service requests.
    /// note that this can be different then the original
    /// token passed for authentication, if the token
    /// needs to be rotated.
    AuthenticationToken: string;

    /// if IsAuthenticated is true, this field contains additional information
    /// about the user, such as name, permissions, etc.
    UserSession: User.IUserSession;
}

export function AuthenticateUserWithToken(request: IAuthenticateUserWithTokenRequest):
    JQueryDeferred<Data.IResponse<IAuthenticateUserWithTokenResponse>> {
    return Util.callWebService(
        "/api/Authentication/AuthenticateUserWithToken",
        "POST",
        request);
}

export interface IAuthenticateUserWithExternalTokenResponse {
    /// if true, then the token is valid and the requested
    /// action can be performed.
    IsAuthenticated: boolean;

    /// if IsAuthenticated is true, this contains the token that must
    /// be passed on all future web service requests.
    AuthenticationToken: string;
    ReportToken: string;

    /// if IsAuthenticated is true, this field contains additional information
    /// about the user, such as name, permissions, etc.
    UserSession: User.IUserSession;
}

export function AuthenticateUserWithExternalToken(request: IAuthenticateUserWithTokenRequest):
    JQueryDeferred<Data.IResponse<IAuthenticateUserWithExternalTokenResponse>> {
    return Util.callWebService(
        "/api/Authentication/AuthenticateUserWithExternalToken",
        "POST",
        request);
}

export interface IEmailPasswordResetRequest {
    UserIdentification: string;
}

export function EmailPasswordReset(request: IEmailPasswordResetRequest): JQueryDeferred<Data.IResponse<string>> {
    return Util.callWebService(
        "/api/Authentication/EmailPasswordReset",
        "POST",
        request);
}

export interface IValidateResetPasswordKeyRequest {
    Token: string;
}

export interface IValidateResetPasswordKeyResponse {
    IsValid: boolean;
    FirstName: string;
    Email: string;
    MinimumPasswordLength: number;
    PasswordComplexityRequirements: string;
}

export function ValidateResetPasswordKey(request: IValidateResetPasswordKeyRequest):
    JQueryDeferred<Data.IResponse<IValidateResetPasswordKeyResponse>> {
    return Util.callWebService(
        "/api/Authentication/ValidateResetPasswordKey",
        "POST",
        request);
}

export interface IResetPasswordRequest {
    Password: string;
    Token: string;
}

export interface IResetPasswordResponse {
    Success: boolean;
    PasswordInvalid: boolean;
    PasswordInvalidReason: string;
    AuthenticationToken: string;
    ReportToken: string;
    UserSession: User.IUserSession;
}

export function ResetPassword(request: IResetPasswordRequest): JQueryDeferred<Data.IResponse<IResetPasswordResponse>> {
    return Util.callWebService(
        "/api/Authentication/ResetPassword",
        "POST",
        request);
}

export function Logout(): JQueryDeferred<any> {
    // Note that we are disabling the error handling.  If the Logout
    // call fails, the user is probably already logged out, so
    // just continue.
    return Util.callWebService(
        "/api/Authentication/Logout",
        "POST",
        null,
        false);
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/data/authenticationData.ts